/**
 * Tier arithmetic: which districts are open, and how many beds they hold.
 *
 * The districts themselves — names, thresholds, ring geometry and the card
 * each hands over — are this game's content, in `src/content/tiers.ts`, which
 * also explains why bed radii are absolute metres.
 */

import { TIERS, type Tier } from '../content/tiers';

/** Highest tier index open at this lifetime tree count. */
export function tierFor(treesGrown: number): number {
  let open = 0;
  for (let i = 0; i < TIERS.length; i++) {
    if (treesGrown >= TIERS[i].treesNeeded) open = i;
  }
  return open;
}

/** Beds available with tiers 0..tier open. */
export function capacityFor(tier: number): number {
  let n = 0;
  for (let i = 0; i <= Math.min(tier, TIERS.length - 1); i++) {
    for (const ring of TIERS[i].rings) n += ring.count;
  }
  return n;
}

/**
 * The smallest tier that has a bed at this index.
 *
 * The counterpart to `tierFor`, and it exists for one reason: `Garden.restore`
 * silently skips a plant whose slot is outside the plot it is restoring into,
 * and a skipped plant is then absent from the next `serialize()` — a player's
 * specimen deleted for good, with nothing said.
 *
 * The tier is derived from a lifetime counter that only ratchets up, so a save
 * should never contain a slot the counter cannot account for. This is what
 * makes that a fact rather than an assumption: the plot is opened to whichever
 * is larger, what the player earned or what their garden actually needs.
 */
export function tierForSlot(slot: number): number {
  for (let i = 0; i < TIERS.length; i++) {
    if (slot < capacityFor(i)) return i;
  }
  return TIERS.length - 1;
}

/** The next district and what it costs, or null once everything is open. */
export function nextTier(treesGrown: number): { tier: Tier; remaining: number } | null {
  const open = tierFor(treesGrown);
  const next = TIERS[open + 1];
  if (!next) return null;
  return { tier: next, remaining: Math.max(0, next.treesNeeded - treesGrown) };
}
